import { styled } from '@mui/system';
import LoadingO2 from '@o2/icons/LoadingO2';
import { BackgroundColorList } from '@o2/ui';
import Stack from '@o2/ui/Stack';
import Box from '@o2/ui/Box';
import { FC, ReactNode } from 'react';

export interface LoadingProps {
  /**
   * Predefined icon sizes for loading
   */
  iconSize?: 'small' | 'medium' | 'large';

  /**
   * Customize the icon size if you need. Overrides the iconSize value.
   */
  customIconSize?: number | string;

  /**
   * Background color. Default: transparent
   */
  backgroundColor?: BackgroundColorList;

  /**
   * Text message below animated logo.
   */
  message?: ReactNode;

  /**
   * @deprecated
   * Background color. Default: transparent
   */
  background?: BackgroundColorList;
}

const LoadingContainer = styled(Stack, {
  name: 'Loading',
  slot: 'root',
  shouldForwardProp: (propName) => propName !== 'backgroundColor',
})<{ backgroundColor?: BackgroundColorList }>(({ backgroundColor, theme }) => ({
  width: '100%',
  height: '100%',
  backgroundColor: backgroundColor ? theme.palette.background[backgroundColor] : 'transparent',
  padding: theme.spacing(1),
}));

const LoadingText = styled(Box, {
  name: 'Loading',
  slot: 'message',
})(({ theme }) => ({
  marginTop: theme.spacing(2),
  color: theme.palette.text.primary,
  textAlign: 'center',
}));

const getIconSize = (iconSize: LoadingProps['iconSize']) => {
  switch (iconSize) {
    case 'small':
      return 24;
    case 'medium':
      return 48;
    case 'large':
      return 72;
    default:
      return iconSize;
  }
};

const Loading: FC<LoadingProps> = ({
  customIconSize,
  iconSize = 'medium',
  backgroundColor,
  message,
  background,
  ...other
}) => {
  return (
    <LoadingContainer
      direction="column"
      justifyContent="center"
      alignItems="center"
      backgroundColor={backgroundColor ?? background}
      {...other}
    >
      <LoadingO2 width={customIconSize ?? getIconSize(iconSize)} />
      {message && <LoadingText>{message}</LoadingText>}
    </LoadingContainer>
  );
};

export default Loading;
